// T078 — Shared time-formatting helpers. Extracted from ApplicationsPage
// so dashboard + applications + documents render dates the same way.
//
// timeAgo  — coarse relative (today / 1d ago / 3w ago / 4mo ago)
// timeAgoFine — finer relative for very recent events (just now / 5m ago /
//   2h ago / yesterday / 3d ago …). Use on items where intra-day freshness
//   matters (toasts, "last touched" on dashboard rows).
//
// Both bail out cleanly on null/invalid input — return '' so the caller
// can render '—' as a fallback.
//
// T123 — date/time policy:
//   • Lists / rows  → timeAgo() (coarse) or timeAgoFine() (intra-day), and put
//     formatDateTime() in the element's `title` so hover reveals the exact time.
//   • Detail views / modals → formatDateTime() (date + time) or formatDate()
//     (date only).
//   • Transient "saved at" / live single-metric indicators → formatClock().
// Don't reach for raw toLocale* in components — route through these so the app
// renders dates one way. (Exceptions: the cover-letter PDF letterhead, which
// needs a long printed date, and the toast-history seconds-precision log.)

export function timeAgo(dateLike) {
  if (!dateLike) return '';
  const then = new Date(dateLike).getTime();
  if (!Number.isFinite(then)) return '';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days < 0) return '';
  if (days === 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export function timeAgoFine(dateLike) {
  if (!dateLike) return '';
  const then = new Date(dateLike).getTime();
  if (!Number.isFinite(then)) return '';
  const diff = Date.now() - then;
  if (diff < 0) return '';
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  const days = Math.floor(diff / 86_400_000);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

// T123 — absolute formatters. Locale-aware (undefined locale → operator's
// browser locale); same null/invalid bailout as the relative helpers.
export function formatDateTime(dateLike) {
  if (!dateLike) return '';
  const d = new Date(dateLike);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(dateLike) {
  if (!dateLike) return '';
  const d = new Date(dateLike);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatClock(dateLike) {
  if (!dateLike) return '';
  const d = new Date(dateLike);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

// T188 — application-deadline urgency. Returns null when there is no date or it
// is too far out to be worth shouting about, so callers can render nothing at
// all rather than a permanently-present neutral chip.
//
// `tone` maps to the theme's semantic tokens, not raw colours: an expired ad is
// a "down" signal, the last 3 days are a warning, the rest is informational.
export function closingIn(dateLike, { warnDays = 3, showWithinDays = 14 } = {}) {
  if (!dateLike) return null;
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;

  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOfDay(d) - startOfDay(new Date())) / 86_400_000);

  if (days < 0) return { days, label: 'closed', tone: 'down', expired: true };
  if (days === 0) return { days, label: 'closes today', tone: 'down', expired: false };
  if (days === 1) return { days, label: 'closes tomorrow', tone: 'warn', expired: false };
  if (days > showWithinDays) return null; // far enough away to be noise
  return {
    days,
    label: `closes in ${days} days`,
    tone: days <= warnDays ? 'warn' : 'info',
    expired: false,
  };
}
